import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, first, takeUntil } from "rxjs";
import { IApiResponseForArray } from "src/app/shared/interfaces/instance.interface";
import { InstanceService } from "src/app/shared/services/instance.service";
import { IExposedUserInOrg, MemberManagementErrorCode } from "../../domain/interfaces/organization.interface";
import { OrganizationService } from "../../domain/services/organization.service";

const SUPER_ADMIN_ROLE = "SuperAdmin";

@Component({
  selector: "app-change-member-role-dialog",
  templateUrl: "./change-member-role-dialog.component.html",
  styleUrls: ["./change-member-role-dialog.component.scss"],
})
export class ChangeMemberRoleDialogComponent implements OnInit, OnDestroy {
  public roles: string[] = [];

  /**
   * Error code returned by the backend for the last change attempt, shown under the role field.
   * Cleared when another role is selected, since the error depends on the chosen role.
   */
  public errorCode: MemberManagementErrorCode | null = null;
  protected onDestroy$ = new Subject<void>();

  public roleForm = this.fb.nonNullable.group({
    role: [this.data.member.roleName, Validators.required],
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly instanceService: InstanceService,
    private readonly organizationService: OrganizationService,
    private readonly translateService: TranslateService,
    public readonly dialogRef: MatDialogRef<ChangeMemberRoleDialogComponent, boolean>,
    private readonly snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public readonly data: { member: IExposedUserInOrg }
  ) {}

  /**
   * Loads the roles that can be given to the member, closing the dialog if they can't be
   * recovered. The SuperAdmin role is only offered to a SuperAdmin, as only they can grant it.
   */
  ngOnInit(): void {
    this.instanceService
      .getRolesWithPermissions()
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe({
        next: (res: IApiResponseForArray) => {
          this.roles = res.roles.map((r) => r.name).filter((name) => name !== SUPER_ADMIN_ROLE || this.canGrantSuperAdmin);
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
          this.dialogRef.close(false);
        },
      });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Whether the user can grant the SuperAdmin role, i.e. is a SuperAdmin themselves (only a
   * SuperAdmin can list every organization).
   */
  get canGrantSuperAdmin(): boolean {
    return this.organizationService.canGetAllOrganizations();
  }

  /**
   * Whether the selected role is the member's current one, in which case there is nothing to save
   */
  get isRoleUnchanged(): boolean {
    return this.roleForm.controls.role.value === this.data.member.roleName;
  }

  /**
   * Sends the member's new role, closing the dialog with `true` on success so the caller reloads
   * the organization. A member no longer in the organization also closes it with `true`, the
   * displayed list being outdated. Known business errors are shown under the role field.
   */
  submit(): void {
    if (this.roleForm.invalid || this.isRoleUnchanged) {
      this.roleForm.markAllAsTouched();
      return;
    }

    this.roleForm.disable();
    const { role } = this.roleForm.getRawValue();

    this.organizationService
      .changeMemberRole(this.data.member.id, role)
      .pipe(
        first(),
        takeUntil(this.onDestroy$),
        finalize(() => this.roleForm.enable())
      )
      .subscribe({
        next: () => {
          this.snackBar.open(`✅ ${this.translateService.instant("administration.changeMemberRole.success")}`, "OK", { duration: 5000 });
          this.dialogRef.close(true);
        },
        error: (err) => {
          if ((err.status === 403 || err.status === 409) && Object.values(MemberManagementErrorCode).includes(err.error)) {
            this.errorCode = err.error;
          } else if (err.status === 404) {
            this.snackBar.open(`❗ ${this.translateService.instant("administration.memberManagement.memberNotFound")}`, "OK", {
              duration: 5000,
            });
            this.dialogRef.close(true);
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
          }
        },
      });
  }
}
